// Shared parsing for year spans expressed relative to TODAY ("this year", "next year", "the next 3 years").
//
// The interpret backend is stateless and its model has no clock, so it CANNOT resolve a now-relative phrase:
// its only relative vocabulary is `year_relative {anchor: first|last|next, count}`, which is anchored to the
// client's capital-plan extent, not to the calendar. Asked for "roofing renewals due this year" the model has
// no matching anchor and falls back to {anchor:"first", count:1} — which the frontend faithfully grounded to
// the FIRST year of the plan (2024), not the current one. The failure is silent: a relative span always
// yields some year, so a misread produces a plausible wrong filter instead of a question.
//
// The frontend is the right place to fix that: it is the only layer that knows both what today is and which
// years the client actually has. This module is the single reader of now-relative phrasing, so the dashboard
// filter path and any future capability (table_view's Year column, the planner) agree on what "this year"
// means. Same role parseMagnitude.js plays for "50k".
//
// MIRROR — the pattern table below is duplicated in the backend at
// `filter-ai/app/capabilities/dashboard_filters/relative_years.py`, which stamps the same reading onto the
// recorded INTENT so traces and the eval don't keep the model's wrong plan-relative guess. This file stays
// the runtime authority (it owns the clock and the client's plan years). The two are kept identical by the
// `xrepo-relative-years` parity gate in `.claude/skills/otto-sync/scripts/run-gates.js`, which compares the
// PARITY-TABLE rows verbatim — edit both sides together and keep the regex source character-for-character
// equal. Behavioural parity is pinned by a shared fixture: `__tests__/relativeYears.fixture.json`, which
// both this suite and `filter-ai/tests/test_relative_years.py` run.

const NUMBER_WORDS = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12
};

// A user-typed calendar year. Its presence means the user named an exact year, so nothing here applies and
// the interpreter's own `year_range` — which is exact — stays authoritative.
const EXPLICIT_YEAR_RE = /\b(?:19|20)\d{2}\b/;

// PARITY-TABLE (mirrored in relative_years.py — see the note above). Ordered most-specific first.
// `start`/`end` are inclusive offsets from the CURRENT year; end === "count" means the row's captured count
// N spans N years starting at `start`.
//
// Deliberately absent: "last / past N years" and "last year". In this product "the last 5 years" means the
// TAIL OF THE PLAN (2031-2035), not 2021-2025 — established by the gold set (year-last-5) and by up-voted
// real usage. Those stay on the plan-relative `year_relative` path; guessing a calendar reading would
// silently break a case that works today. Absolute phrasing is excluded by EXPLICIT_YEAR_RE above.
const PATTERNS = [
    // "the next 3 years", "over the next three years", "within the next 2 years", "coming 5 years".
    // A forward span INCLUDES the current year (user decision), matching the dashboard's existing
    // `anchor:"next"` grounding: "the next 3 years" in 2026 is 2026-2028.
    { id: "next-n-years", re: /\b(?:the\s+)?(?:next|coming|upcoming|following)\s+(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+years?\b/i, start: 0, end: "count" },
    // "next year", "the following year" — the year AFTER this one. "coming/upcoming year" is left out on
    // purpose: it usually means the next twelve months (i.e. now), which is a different window.
    { id: "next-year", re: /\b(?:the\s+)?(?:next|following)\s+year\b/i, start: 1, end: 1 },
    // "this year", "the current year", "this fiscal/calendar year", "due this year".
    { id: "this-year", re: /\b(?:this|the\s+current|current)\s+(?:calendar\s+|fiscal\s+)?year\b/i, start: 0, end: 0 },
    // "year to date" / "YTD".
    { id: "year-to-date", re: /\byear[-\s]to[-\s]date\b|\bytd\b/i, start: 0, end: 0 },
    // "by the end of the year", "before year-end".
    { id: "year-end", re: /\b(?:by|before)\s+(?:the\s+)?(?:end\s+of\s+(?:the\s+|this\s+)?year|year[-\s]end)\b/i, start: 0, end: 0 },
    // "the rest of this year", "the remainder of the year".
    { id: "rest-of-year", re: /\b(?:rest|remainder)\s+of\s+(?:the|this)\s+year\b/i, start: 0, end: 0 }
];

/**
 * Read a now-relative year span out of the user's raw utterance.
 *   parseRelativeYears("roofing renewals due this year") -> { startOffset: 0, endOffset: 0, phrase: "this year" }
 *   parseRelativeYears("HVAC over the next 3 years")     -> { startOffset: 0, endOffset: 2, phrase: "the next 3 years" }
 *   parseRelativeYears("what's planned next year")       -> { startOffset: 1, endOffset: 1, phrase: "next year" }
 *   parseRelativeYears("show me 2026 to 2028")           -> null   (explicit years win)
 *   parseRelativeYears("the last 3 years")               -> null   (plan-relative — see the table note)
 * Offsets are years from the CURRENT year and are inclusive on both ends; the caller adds the clock and
 * validates against the client's real plan years. Only the first match is read — a compound span
 * ("this year and next") is beyond what a single filter range can express anyway.
 */
export const parseRelativeYears = utterance => {
    const text = String(utterance == null ? "" : utterance);
    if (!text.trim() || EXPLICIT_YEAR_RE.test(text)) return null;
    for (const row of PATTERNS) {
        const m = text.match(row.re);
        if (!m) continue;
        let end = row.end;
        if (end === "count") {
            const raw = (m[1] || "").toLowerCase();
            const count = NUMBER_WORDS[raw] != null ? NUMBER_WORDS[raw] : parseInt(raw, 10);
            if (!Number.isFinite(count) || count < 1) continue;
            end = row.start + count - 1;
        }
        return { startOffset: row.start, endOffset: end, phrase: m[0].trim() };
    }
    return null;
};

// Exposed for the parity/self-consistency tests only (the spelled-out counts must all be reachable by the
// counted pattern's own alternation).
export const __TABLE__ = { NUMBER_WORDS, PATTERNS };
